/**
 * web/router.ts
 * Tiny SPA router replacing expo-router for the Vite bundle.
 *
 * Holds a registry of `path -> Route` factories. `navigate(path, ...)`
 * pushes to `history.pushState`, looks up the matching route, destroys
 * the previous handle, and mounts the new one into the shared root
 * element. `popstate` (browser back/forward) is hooked up automatically.
 *
 * Paths support a single trailing wildcard segment if needed in future, but
 * for now every screen is a literal path match. Query strings are passed to
 * the route factory as a {@link URLSearchParams}.
 *
 * @module web/router
 */

/// <reference lib="dom" />

/**
 * Factory for a screen. Receives the active router (so it can wire
 * navigation callbacks) plus the parsed query string. Returns a handle
 * whose `destroy()` the router calls before mounting the next screen.
 */
export type Route = (router: Router, params: URLSearchParams) => RouteHandle;

/** Mounted-screen handle. */
export interface RouteHandle {
  /** Tear down listeners, RAF loops, etc. before the next screen mounts. */
  destroy: () => void;
}

export interface Router {
  /** Navigate to a path, optionally with a query-string object. */
  navigate: (path: string, query?: Record<string, string>) => void;
  /** Pop the current screen and go to the previous one (or `fallback`). */
  back: (fallback?: string) => void;
  /** Get the current path. */
  currentPath: () => string;
  /** Tear down the router and the active screen. */
  destroy: () => void;
}

/** Internal: the screen-pair tracking variables — kept together to make the
 *  destroy/swap dance obvious. */
interface ActiveScreen {
  path: string;
  handle: RouteHandle;
}

/**
 * Create and start a SPA router.
 *
 * @param root The DOM node to mount screens into.
 * @param routes A map of `path -> Route` factories.
 * @param fallback Path to use when no route matches (and on initial load if
 *   `location.pathname` doesn't match anything). Defaults to `'/'`.
 */
export function createRouter(
  root: HTMLElement,
  routes: Record<string, Route>,
  fallback: string = '/',
): Router {
  let active: ActiveScreen | null = null;

  const resolve = (path: string): { matchedPath: string; route: Route } => {
    if (Object.hasOwn(routes, path)) {
      return { matchedPath: path, route: routes[path] as Route };
    }
    if (Object.hasOwn(routes, fallback)) {
      return { matchedPath: fallback, route: routes[fallback] as Route };
    }
    throw new Error(
      `Router: no route for '${path}' and fallback '${fallback}' is not registered`,
    );
  };

  const mount = (path: string, query: URLSearchParams): void => {
    if (active) {
      active.handle.destroy();
      active = null;
    }
    root.innerHTML = '';
    const { matchedPath, route } = resolve(path);
    // DECISION: Build the router handle first so the route receives a stable
    // reference even on the very first mount. `routerHandle` is assigned
    // before the initial mount happens below.
    const handle = route(routerHandle, query);
    // DECISION: If the route synchronously called router.navigate() during its
    // own factory (e.g. a redirect based on params), a nested mount() already
    // installed the new screen into `active`. Tear down the orphaned outer
    // handle instead of overwriting the inner one — otherwise the inner
    // screen's listeners would leak and destroy() would tear down the wrong
    // handle on the next navigation.
    if (active) {
      handle.destroy();
      return;
    }
    active = { path: matchedPath, handle };
  };

  const mountFromLocation = (): void => {
    const params = new URLSearchParams(globalThis.location.search);
    mount(globalThis.location.pathname, params);
  };

  const onPopState = (): void => {
    mountFromLocation();
  };

  globalThis.addEventListener('popstate', onPopState);

  const navigate: Router['navigate'] = (path, query) => {
    const search = query ? `?${new URLSearchParams(query).toString()}` : '';
    globalThis.history.pushState({}, '', `${path}${search}`);
    mount(path, new URLSearchParams(search));
  };

  const back: Router['back'] = (fb = '/') => {
    // DECISION: history.length === 1 means we landed here directly (no prior
    // entry to pop back to). Navigate to the fallback instead of leaving the
    // user on a stuck "back" button.
    if (globalThis.history.length > 1) {
      globalThis.history.back();
    } else {
      navigate(fb);
    }
  };

  const routerHandle: Router = {
    navigate,
    back,
    currentPath: () => (active ? active.path : fallback),
    destroy: () => {
      globalThis.removeEventListener('popstate', onPopState);
      if (active) {
        active.handle.destroy();
        active = null;
      }
      root.innerHTML = '';
    },
  };

  // Initial mount — read whatever path the page loaded at. Runs after
  // `routerHandle` is assigned so the first route can navigate immediately.
  mountFromLocation();

  return routerHandle;
}
